import type { DungeonEntity } from '../entities/DungeonEntity';
import type { EffectCondition } from '../types/effect.types';
import type { ConfigParams } from '../types/effect.types';
import { DungeonEntityType } from '../types/enums';
import { chebyshevDistance } from '../utils/distance.utils';
import { msToTicks } from '../utils/timing.utils';
import { dungeonService } from '../services/DungeonService';

export const CONDITION_CHECKERS: Record<
  string,
  (
    entity: DungeonEntity,
    params?: ConfigParams,
    caster?: DungeonEntity,
  ) => boolean
> = {
  shieldNotFull: (e) => e.shield < e.maxShield,
  shieldEmpty: (e) => e.shield === 0,
  hpBelow: (e, p) => (e.life / e.maxLife) * 100 < (p?.threshold as number),
  hpAbove: (e, p) => (e.life / e.maxLife) * 100 > (p?.threshold as number),
  hasStatus: (e, p) => e.getActiveBuffs().some((b) => b.def.id === p?.buffId),
  noStatus: (e, p) => !e.getActiveBuffs().some((b) => b.def.id === p?.buffId),
  // In damageMultiplierConditions this reads the *previous* hit's spell —
  // lastDamageSourceSpellId is assigned after computeDamageMultiplier runs
  // (dealDamage in effectHandlers.ts). Safe in onKill / post-damage hooks.
  currentSpellId: (e, p) =>
    (e as { lastDamageSourceSpellId?: string }).lastDamageSourceSpellId ===
    (p?.spellId as string),
  statusElapsedAtLeast: (e, p) => {
    const b = e.getActiveBuffs().find((x) => x.def.id === p?.buffId);
    if (!b) {
      return false;
    }
    const total = msToTicks(b.def.durationMs ?? 0);
    const elapsed = total - (b.remainingTicks ?? 0);
    return elapsed >= msToTicks((p?.ms as number) ?? 0);
  },
  statusStacksAtLeast: (e, p) => {
    const b = e.getActiveBuffs().find((x) => x.def.id === p?.buffId);
    return !!b && (b.stacks ?? 1) >= ((p?.n as number) ?? 1);
  },
  isNearestEnemy: (target, _p, caster) => {
    if (!caster) {
      return false;
    }
    const cx = caster.positionX;
    const cy = caster.positionY;
    const targetDist = chebyshevDistance(
      cx,
      cy,
      target.positionX,
      target.positionY,
    );
    for (const entity of dungeonService.getEntities()) {
      if (entity.id === caster.id || entity.id === target.id) {
        continue;
      }
      if (entity.type !== DungeonEntityType.MONSTER) {
        continue;
      }
      if (
        chebyshevDistance(cx, cy, entity.positionX, entity.positionY) <
        targetDist
      ) {
        return false;
      }
    }
    return true;
  },
};

/** Returns true if all non-timing conditions pass. */
export function checkEffectConditions(
  entity: DungeonEntity,
  conditions?: EffectCondition[],
  caster?: DungeonEntity,
): boolean {
  return (conditions ?? []).every(
    (c) => CONDITION_CHECKERS[c.check]?.(entity, c.params, caster) ?? false,
  );
}

/** Evaluates a single condition (used for castCondition in SpellService). */
export function checkCondition(
  entity: DungeonEntity,
  c: EffectCondition,
): boolean {
  return CONDITION_CHECKERS[c.check]?.(entity, c.params) ?? false;
}
